import { useEffect, useMemo } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  startOfMonth,
  endOfMonth,
  endOfDay,
  addMonths,
  subMonths,
  formatISO,
  startOfDay,
} from 'date-fns';

import { useSelector } from 'react-redux';

import { EventListItem } from '../types/events.types';
import { EventStatus } from 'routes/referral/types/event-detail-api.types';
import { IState } from 'models/reducers/i-state.interface';

import calendarEventImage from 'assets/events/calendar-event.png';
import wofEventImage from 'assets/events/wof-event.png';
import { fetchCalendar } from 'routes/dailyRewards/dailyRewards.service';
import { DailyLoginCalendarResponse } from 'routes/dailyRewards/dailyRewards.models';
import { queryKeys } from 'hooks/queries/query-keys';
import { createCustomEvent } from '../utils/customEvent';

const WHEEL_OF_FORTUNE_ID = -1;
const DAILY_REWARDS_ID_BASE = -1000;
const PAST_MONTHS_TO_CHECK = 6;

const hasRewards = (response: DailyLoginCalendarResponse): boolean => {
  return response.days.some((day) => day.current_month && day.reward !== null);
};

type MonthQuery = {
  year: number;
  month: number;
  date: Date;
  offset: number; // -N for past, 0 for current, 1 for next
};

const generateWheelOfFortuneEvent = (now: Date): EventListItem => {
  const todayStart = startOfDay(now);
  return createCustomEvent({
    id: WHEEL_OF_FORTUNE_ID,
    name: 'Wheel of Fortune',
    _customAction: 'wheel-of-fortune',
    image_url: wofEventImage,
    status: EventStatus.ONGOING,
    start_datetime: formatISO(todayStart),
    end_datetime: formatISO(endOfDay(now)),
    is_active: true,
  });
};

export const useCustomEvents = () => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const now = useMemo(() => new Date(), []);

  const monthQueries = useMemo((): MonthQuery[] => {
    const queries: MonthQuery[] = [];

    // Past months
    for (let i = PAST_MONTHS_TO_CHECK; i >= 1; i--) {
      const date = subMonths(now, i);
      queries.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        date,
        offset: -i,
      });
    }

    // Current month
    queries.push({
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      date: now,
      offset: 0,
    });

    // Next month
    const next = addMonths(now, 1);
    queries.push({
      year: next.getFullYear(),
      month: next.getMonth() + 1,
      date: next,
      offset: 1,
    });

    return queries;
  }, [now]);

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dailyRewards.all });
  }, [walletAddress, queryClient]);

  const calendarResults = useQueries({
    queries: monthQueries.map((mq) => ({
      queryKey: queryKeys.dailyRewards.calendar(mq.year, mq.month),
      queryFn: () => fetchCalendar(mq.year, mq.month),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const customEvents = useMemo(() => {
    const events: EventListItem[] = [];

    // Wheel of Fortune — always one active event
    events.push(generateWheelOfFortuneEvent(now));

    // Daily Rewards — only for months where the API confirms rewards exist
    monthQueries.forEach((mq, index) => {
      const result = calendarResults[index];
      if (!result.data || !hasRewards(result.data)) return;

      const monthStart = startOfMonth(mq.date);
      const monthEnd = endOfMonth(mq.date);

      let status: EventStatus;
      let isActive: boolean;

      if (mq.offset === 0) {
        status = EventStatus.ONGOING;
        isActive = true;
      } else if (mq.offset > 0) {
        status = EventStatus.UPCOMING;
        isActive = false;
      } else {
        status = EventStatus.FINISHED;
        isActive = false;
      }

      events.push(
        createCustomEvent({
          id: DAILY_REWARDS_ID_BASE - (mq.offset + PAST_MONTHS_TO_CHECK),
          name: 'Daily Rewards',
          _customAction: 'daily-rewards',
          image_url: calendarEventImage,
          status,
          start_datetime: formatISO(monthStart),
          end_datetime: formatISO(monthEnd),
          is_active: isActive,
        })
      );
    });

    return events;
  }, [now, monthQueries, calendarResults]);

  return customEvents;
};
